"use client";

import styled from "styled-components";
import { useTranslations } from "next-intl";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { useUserProfile } from "@/lib/hooks/useUser";
import {
  appVersion,
  insightsProductCode,
  patientPathProductCode,
} from "@/lib/constants/appCodes";
import { getEmeaContactPhoneKey } from "@/lib/constants/emeaCountries";

const ContactModalBody = styled.div`
  max-height: 50vh;
  overflow: auto;

  a,
  span {
    font-weight: bold;
  }

  a {
    text-decoration: underline;
    color: ${(props) => props.theme.black};
  }

  @media (max-width: 425px) {
    max-height: 86vh;
  }
`;

type ContactUsContentProps = {
  // Override for the public /legal/contact-us page, which drives contact
  // info from the selected locale rather than geo-IP detection.
  // The in-app modal passes nothing and keeps
  // its existing store-driven behavior exactly as before.
  userCountry?: string;
};

export function ContactUsContent({
  userCountry: userCountryOverride,
}: ContactUsContentProps = {}) {
  const t = useTranslations("contactUs");
  const tAnz = useTranslations("anzContactUs");
  const storeUserCountry = useBoundStore((state) => state.userCountry);
  const userCountry = userCountryOverride ?? storeUserCountry;

  // patient-specific
  const { userProfileQuery } = useUserProfile();
  const userRole = userProfileQuery.data?.role;

  const isSea = ["MY", "SG"].includes(userCountry);
  const isEmea = ["GB", "AT", "CH", "DE", "IE"].includes(userCountry);
  const emeaPhoneKey = getEmeaContactPhoneKey(userCountry);

  const productCodes = (
    <p>
      {t("patientPathCode")}
      <span style={{ fontFamily: "Arial" }}>
        &nbsp;{patientPathProductCode}
      </span>
      <br />
      {t("insightsCode")}
      <span style={{ fontFamily: "Arial" }}>&nbsp;{insightsProductCode}</span>
      <br />
      <br />
      {`${t("version")} ${appVersion}`}
    </p>
  );

  return (
    <ContactModalBody>
      {isSea ? (
        <>
          <p>
            {t("intro1")}&nbsp;
            <a href={`mailto:${t("seaContactEmail")}`}>
              {t("seaContactEmail")}
            </a>
            &nbsp;
            {t("intro2")}
          </p>
          <br />
          <p>
            {t("customerHoursText")}&nbsp;
            <span>{t("seaCustomerHours")}</span>
          </p>
          <br />
          {productCodes}
        </>
      ) : isEmea ? (
        <>
          <p>
            {t("intro1")}&nbsp;
            <a href={`mailto:${t("emeaContactEmail")}`}>
              {t("emeaContactEmail")}
            </a>
            &nbsp;
            {t("intro2")}
          </p>
          <br />
          <p>
            {t("callUsText")}&nbsp;
            <a href={`tel:${t(emeaPhoneKey)}`}>{t(emeaPhoneKey)}</a>&nbsp;
          </p>
          <p>
            {t("customerHoursText")}&nbsp;
            <span>{t("emeaCustomerHours")}</span>
          </p>
          <br />
          {productCodes}
        </>
      ) : userRole === "patient" && userCountry === "AU" ? (
        <>
          <p>
            {tAnz.rich("description", {
              link: (chunks) => <a href={`mailto:${chunks}`}>{chunks}</a>,
            })}
          </p>
          <br />
          <p>{tAnz("description2")}&nbsp;</p>
          <br />
          <p>
            {t.rich("patientPathCode", {
              link: (chunks) => (
                <span style={{ fontFamily: "Arial" }}>{chunks}</span>
              ),
            })}
            <br />
            {t.rich("insightsCode", {
              link: (chunks) => (
                <span style={{ fontFamily: "Arial" }}> {chunks}</span>
              ),
            })}
            <br />
            <br />
            {t("version")}
          </p>
        </>
      ) : (
        <>
          <p>
            {t("intro1")}&nbsp;
            <a href="mailto:info@velysinsights.com">{t("contactEmail")}</a>
            &nbsp;
            {t("intro2")}
          </p>
          <br />
          <p>
            {t("callUsText")}&nbsp;
            <a href="tel:+18006890746">{t("contactPhone")}</a>&nbsp;
          </p>
          <p>
            {t("customerHoursText")}&nbsp;
            <span>{t("customerHours")}</span>
          </p>
          <br />
          {productCodes}
        </>
      )}
    </ContactModalBody>
  );
}
